/**
 * AgencyClientsTab.jsx
 * ------------------------------------------------------------------
 * Lists agency clients + supports CRUD inline and a CSV import flow
 * with column drag-mapper (user picks which CSV column maps to each
 * canonical field).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Plus, Upload, Loader2, Trash2, Edit3, FileText, Download,
  X, Check, AlertTriangle, Globe, Share2,
} from "lucide-react";
import AgencyShareLinkModal from "./AgencyShareLinkModal";

const FIELDS = [
  { key: "name", label: "Názov klienta", required: true },
  { key: "domain", label: "Doména", required: true },
  { key: "language", label: "Jazyk" },
  { key: "industry", label: "Odvetvie" },
  { key: "contact_email", label: "Kontaktný email" },
  { key: "notes", label: "Poznámky" },
];

export default function AgencyClientsTab({ userEmail, onClientChanged }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [csv, setCsv] = useState(null);  // {headers, sample, suggestions, total_rows, raw}
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [shareClient, setShareClient] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/agency/clients`, {
        headers: { "X-User-Email": userEmail },
      });
      setClients(r.data?.clients || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { if (userEmail) load(); }, [userEmail]);

  const handleDelete = async (id) => {
    if (!confirm("Naozaj zmazať tohto klienta?")) return;
    try {
      await axios.delete(`${API}/agency/clients/${id}`, {
        headers: { "X-User-Email": userEmail },
      });
      await load();
      onClientChanged?.();
    } catch {}
  };

  const handleCsvFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = reader.result;
      // Build a FormData for the preview endpoint, which expects multipart.
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await axios.post(`${API}/agency/csv-preview`, fd, {
          headers: { "X-User-Email": userEmail, "Content-Type": "multipart/form-data" },
        });
        setCsv({ ...r.data, raw });
        setMapping(r.data.suggestions || {});
      } catch (e) {
        alert("CSV preview zlyhalo: " + (e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message));
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csv) return;
    if (mapping.name == null || mapping.domain == null) {
      alert("Vyber stĺpec pre Názov aj Doménu.");
      return;
    }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/agency/csv-import`, {
        content: csv.raw,
        mapping,
        default_language: "sk",
      }, { headers: { "X-User-Email": userEmail } });
      alert(`Importovaných ${r.data.created} klientov · ${r.data.skipped?.length || 0} preskočených.`);
      setCsv(null); setMapping({});
      await load();
      onClientChanged?.();
    } catch (e) {
      alert("Import zlyhal: " + (e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="agency-clients-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold">Klienti agentúry ({clients.length})</h2>
        <div className="flex items-center gap-2">
          <label className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm cursor-pointer inline-flex items-center gap-1" data-testid="agency-csv-upload-label">
            <Upload className="w-3.5 h-3.5" /> Importovať CSV
            <input
              type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => handleCsvFile(e.target.files?.[0])}
              data-testid="agency-csv-upload-input"
            />
          </label>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="agency-add-client-btn"
            className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-sm inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Pridať klienta
          </button>
        </div>
      </div>

      {loading && <div className="text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítam…</div>}

      {/* CSV drag-mapper modal */}
      {csv && (
        <div className="p-5 bg-gray-900/40 border border-purple-700/40 rounded-sm" data-testid="agency-csv-mapper">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-purple-400">CSV → Klienti</div>
              <div className="text-sm font-bold">Namapuj stĺpce ({csv.total_rows} riadkov)</div>
            </div>
            <button onClick={() => { setCsv(null); setMapping({}); }} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {FIELDS.map(f => (
              <label key={f.key} className="text-xs">
                <span className="text-gray-400 block mb-1">
                  {f.label}{f.required && <span className="text-rose-400"> *</span>}
                </span>
                <select
                  value={mapping[f.key] ?? ""}
                  onChange={(e) => setMapping(prev => ({ ...prev, [f.key]: e.target.value === "" ? null : Number(e.target.value) }))}
                  data-testid={`agency-csv-map-${f.key}`}
                  className="w-full px-2 py-1 bg-black/50 border border-gray-800 focus:border-purple-500 text-gray-200 rounded-sm"
                >
                  <option value="">— nepoužiť —</option>
                  {csv.headers.map((h, i) => (
                    <option key={i} value={i}>{i + 1}. {h || `(stĺpec ${i + 1})`}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {/* Preview rows */}
          <div className="overflow-x-auto border border-gray-900 rounded-sm mb-4">
            <table className="w-full text-xs">
              <thead className="bg-black/50 text-gray-500 uppercase tracking-widest">
                <tr>{csv.headers.map((h, i) => <th key={i} className="px-2 py-1 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {csv.sample.map((row, ri) => (
                  <tr key={ri} className="border-t border-gray-900">
                    {row.map((cell, ci) => <td key={ci} className="px-2 py-1 text-gray-300 max-w-[180px] truncate">{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleImport}
            disabled={importing || mapping.name == null || mapping.domain == null}
            data-testid="agency-csv-import-btn"
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
          >
            {importing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Importujem…</> : <><Check className="w-3.5 h-3.5" /> Importovať {csv.total_rows} klientov</>}
          </button>
        </div>
      )}

      {/* Add client inline form */}
      {showAdd && <AddClientForm onCancel={() => setShowAdd(false)} userEmail={userEmail} onSaved={() => { setShowAdd(false); load(); onClientChanged?.(); }} />}

      {/* Client table */}
      {clients.length > 0 && (
        <div className="overflow-x-auto border border-gray-800 rounded-sm" data-testid="agency-clients-table">
          <table className="w-full text-xs">
            <thead className="bg-black/50 text-gray-500 uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 text-left">Klient</th>
                <th className="px-3 py-2 text-left">Doména</th>
                <th className="px-3 py-2 text-left">Jazyk</th>
                <th className="px-3 py-2 text-center">Skóre</th>
                <th className="px-3 py-2 text-center">GEO pages</th>
                <th className="px-3 py-2 text-right">Akcie</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c, i) => (
                <tr key={c.id} className="border-t border-gray-900 hover:bg-black/30" data-testid={`agency-client-row-${i}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-white">{c.name}</div>
                    <div className="text-[10px] text-gray-500">{c.industry || "—"}</div>
                  </td>
                  <td className="px-3 py-2">
                    <a href={c.domain.startsWith("http") ? c.domain : `https://${c.domain}`} target="_blank" rel="noreferrer" className="text-purple-400 hover:underline inline-flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {c.domain.replace(/^https?:\/\//, "")}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-gray-400 uppercase">{c.language || "—"}</td>
                  <td className="px-3 py-2 text-center tabular-nums">
                    {c.latest_scan ? <span className="text-purple-300 font-bold">{c.latest_scan.summary?.score ?? "—"}</span> : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">{c.geo_pages_count || 0}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a
                      href={`${API}/agency/clients/${c.id}/report.pdf`}
                      onClick={(e) => {
                        e.preventDefault();
                        axios.get(`${API}/agency/clients/${c.id}/report.pdf`, {
                          headers: { "X-User-Email": userEmail },
                          responseType: "blob",
                        }).then(r => {
                          const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                          window.open(url, "_blank");
                        });
                      }}
                      data-testid={`agency-client-pdf-${i}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm"
                    >
                      <FileText className="w-3 h-3" /> PDF
                    </a>
                    <button
                      onClick={() => setShareClient(c)}
                      data-testid={`agency-client-share-${i}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-900/40 hover:bg-purple-900/70 text-purple-200 rounded-sm"
                      title="Share link pre klienta"
                    >
                      <Share2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      data-testid={`agency-client-delete-${i}`}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-900/40 hover:bg-rose-900 text-rose-200 rounded-sm"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && clients.length === 0 && !showAdd && !csv && (
        <div className="p-8 text-center bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="agency-clients-empty">
          <AlertTriangle className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <div className="text-gray-400 text-sm">Žiadni klienti. Pridaj manuálne alebo importuj CSV.</div>
        </div>
      )}

      <AgencyShareLinkModal
        open={!!shareClient}
        onClose={() => setShareClient(null)}
        client={shareClient}
        userEmail={userEmail}
      />
    </div>
  );
}

function AddClientForm({ onCancel, userEmail, onSaved }) {
  const [form, setForm] = useState({ name: "", domain: "", language: "sk", industry: "", contact_email: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/agency/clients`, form, {
        headers: { "X-User-Email": userEmail },
      });
      onSaved?.();
    } catch (e) {
      alert("Uloženie zlyhalo: " + (e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <div className="p-4 bg-gray-900/40 border border-purple-700/40 rounded-sm space-y-2" data-testid="agency-add-client-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input label="Názov klienta *" value={form.name} onChange={(v) => setForm(s => ({ ...s, name: v }))} testid="agency-add-name" />
        <Input label="Doména *" value={form.domain} onChange={(v) => setForm(s => ({ ...s, domain: v }))} testid="agency-add-domain" />
        <Input label="Jazyk" value={form.language} onChange={(v) => setForm(s => ({ ...s, language: v }))} testid="agency-add-language" />
        <Input label="Odvetvie" value={form.industry} onChange={(v) => setForm(s => ({ ...s, industry: v }))} testid="agency-add-industry" />
        <Input label="Email kontakt" value={form.contact_email} onChange={(v) => setForm(s => ({ ...s, contact_email: v }))} testid="agency-add-email" />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={save}
          disabled={saving || !form.name.trim() || !form.domain.trim()}
          data-testid="agency-add-save-btn"
          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-sm font-bold rounded-sm inline-flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Uložiť
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm">Zrušiť</button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="text-xs">
      <span className="text-gray-400 block mb-1">{label}</span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-2 py-1.5 bg-black/50 border border-gray-800 focus:border-purple-500 text-white rounded-sm focus:outline-none"
      />
    </label>
  );
}
